/**
 * Prices for one variant. Rows are never updated — setting a price closes the current row and
 * inserts a new one server-side, which is why `setPrice`/`setFloor` both return a new id and
 * every write here invalidates the whole per-variant list rather than one row.
 */

import { api } from '../api.js';
import { priceListTag } from '../tags.js';
import type { AdminPriceType, PriceId, PriceRow, ShopId, VariantId } from '../../types/api.js';

export type SetPriceBody = {
  readonly variantId: VariantId;
  readonly shopId?: ShopId | undefined;
  readonly priceType?: AdminPriceType | undefined;
  readonly minQty?: number | undefined;
  readonly amountKes: number;
  readonly floorKes?: number | undefined;
  readonly note?: string | undefined;
};

export type SetFloorBody = {
  readonly variantId: VariantId;
  readonly shopId?: ShopId | undefined;
  readonly priceType?: AdminPriceType | undefined;
  readonly minQty?: number | undefined;
  readonly floorKes?: number | undefined;
};

/** What one item sells for at one shop, as the till would charge it. */
export type ResolvedPrice = {
  readonly variantId: VariantId;
  readonly amountKes: number;
  readonly floorKes?: number | null;
  readonly source: string;
  /** The quantity tier that applied — for wholesale, "from this many pieces". */
  readonly appliedMinQty: number;
};

export type ResolvePriceListBody = {
  readonly variantIds: readonly VariantId[];
  readonly shopId?: ShopId | undefined;
  readonly priceType: AdminPriceType;
  readonly quantity?: number | undefined;
};

export const pricesApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Many items priced in one request. An item with no price of this type is absent from the
     * answer rather than zero — a zero would be a price someone could sell at.
     */
    resolvePriceList: build.query<ResolvedPrice[], ResolvePriceListBody>({
      query: (body) => ({ url: '/prices/resolve-list', method: 'POST', body }),
      providesTags: (_result, _error, { variantIds }) => variantIds.map((id) => priceListTag(id)),
    }),

    listVariantPrices: build.query<PriceRow[], VariantId>({
      query: (variantId) => `/prices/variant/${variantId}`,
      providesTags: (_result, _error, variantId) => [priceListTag(variantId)],
    }),

    listVariantPriceHistory: build.query<PriceRow[], { variantId: VariantId; shopId?: ShopId | undefined }>({
      query: ({ variantId, shopId }) => ({
        url: `/prices/variant/${variantId}/history`,
        params: shopId === undefined ? {} : { shopId },
      }),
      providesTags: (_result, _error, { variantId }) => [priceListTag(variantId)],
    }),

    setPrice: build.mutation<PriceId, SetPriceBody>({
      query: (body) => ({ url: '/prices', method: 'POST', body }),
      invalidatesTags: (_result, _error, { variantId }) => [priceListTag(variantId)],
    }),

    setPriceFloor: build.mutation<PriceId, SetFloorBody>({
      query: (body) => ({ url: '/prices/floor', method: 'PUT', body }),
      invalidatesTags: (_result, _error, { variantId }) => [priceListTag(variantId)],
    }),

    /** Closes the row rather than deleting it — the server keeps it as history. */
    closePrice: build.mutation<void, { priceId: PriceId; variantId: VariantId }>({
      query: ({ priceId }) => ({ url: `/prices/${priceId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { variantId }) => [priceListTag(variantId)],
    }),
  }),
});

export const {
  useListVariantPricesQuery,
  useResolvePriceListQuery,
  useListVariantPriceHistoryQuery,
  useSetPriceMutation,
  useSetPriceFloorMutation,
  useClosePriceMutation,
} = pricesApi;
